/**
 * useUnlockContent
 *
 * Reactive hook providing unlock-content state and the executeUnlock action
 * for a specific media item.
 *
 * Uses useListenToSecureStorageUpdates so the component automatically re-renders
 * whenever the user unlocks content or gains subscription access (e.g. after
 * initial sync or real-time Firestore updates).
 *
 * Flutter: ContentViewModel unlock-related state + ExclusiveContentNotifier
 */
import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/presentation/store';
import { useListenToSecureStorageUpdates } from '@/data/storage/local/secureStorageWebCrypto';
import type { UnlockedContentStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { UnlockContentService } from '@/domain/services/content/unlockContentService';
import { UnlockContentUseCase } from '@/domain/usecases/content/unlockContentUseCase';
import { selectWalletCoins } from '@/presentation/store/slices/wallet/walletSlice';
import { selectHasActiveSubscription } from '@/presentation/store/slices/exclusiveContent/exclusiveContentSlice';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';

const unlockContentService = new UnlockContentService();
const unlockContentUseCase = new UnlockContentUseCase(unlockContentService);

export function useUnlockContent(media: UserMediaEntity) {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const walletCoins = useSelector(selectWalletCoins);

  // Reactive: re-renders when this specific content is unlocked
  const unlockedKey = SECURE_STORAGE_KEYS.UNLOCKED_CONTENT + media.relativePath;
  const { data: unlockedData } = useListenToSecureStorageUpdates<UnlockedContentStorage>(unlockedKey);

  // Reactive: re-renders when the slice updates this creator's subscription.
  // Single source of truth — ExclusiveContentListener hydrates the slice from
  // SecureStorage and keeps it in sync.
  const hasActiveSubscription = useSelector((state: RootState) =>
    selectHasActiveSubscription(state, media.firebaseId ?? ''),
  );

  const [isUnlocking, setIsUnlocking] = useState(false);

  const isOwner = currentUser?.firebaseId === media.firebaseId;

  const hasUnlockedContent = unlockedData !== null && unlockedData.path === media.relativePath;

  const hasExclusiveRights = unlockContentService.hasExclusiveRights({
    isOwner,
    hasUnlockedContent,
    hasActiveSubscription,
    typeLimit: media.typeLimit,
    duration: media.duration,
  });

  const canUnlock = unlockContentService.canUnlockContent({
    allowUnlock: media.allowUnlock,
    unlockCharge: media.unlockCharge,
    isOwner,
    hasUnlockedContent,
    hasActiveSubscription,
    typeLimit: media.typeLimit,
    duration: media.duration,
  });

  const shouldRecharge = (media.unlockCharge ?? 0) > walletCoins;
  const rechargeAmount = shouldRecharge ? Math.ceil((media.unlockCharge ?? 0) - walletCoins) : 0;

  const executeUnlock = useCallback(async (): Promise<boolean> => {
    if (!currentUser) return false;
    setIsUnlocking(true);

    try {
      const success = await unlockContentUseCase.execute(
        media,
        currentUser,
        hasUnlockedContent,
        hasActiveSubscription,
      );

      return success;
    } catch {
      return false;
    } finally {
      setIsUnlocking(false);
    }
  }, [currentUser, media, hasUnlockedContent, hasActiveSubscription]);

  return {
    hasExclusiveRights,
    canUnlock,
    hasUnlockedContent,
    hasActiveSubscription,
    isOwner,
    shouldRecharge,
    rechargeAmount,
    walletCoins,
    isUnlocking,
    executeUnlock,
  };
}
